import {getCommentEditorKeyForElement} from 'components/DSYS/editor/utils/commentEditorRoots';
import {filterElementsToPlanningCommentOrigin} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {$getRoot, type LexicalEditor} from 'lexical';
import {EnhancedHeadingNode} from 'plugins/enhancedHeadings/EnhancedHeadingNode';
import {setPersistedHeadingCollapsed} from 'plugins/enhancedHeadings/HeadingCollapsePersistence';

export interface CommentForDrawer {
  threadId: string;
  originalText: string;
  elementCount: number; // Number of DOM elements with this thread ID
}

const getCommentElementSelector = (threadId: string) => {
  const escapedThreadId = CSS.escape(threadId);
  return `[data-highlight-id~="${escapedThreadId}"], [data-comment-id~="${escapedThreadId}"]`;
};

type CommentAnchorOptions = {
  includeHiddenFallback?: boolean;
  editorKey?: string;
  originId?: string | null;
};

const belongsToEditorKey = (element: HTMLElement, editorKey: string | undefined): boolean =>
  !editorKey || getCommentEditorKeyForElement(element) === editorKey;

const getCommentElements = (
  threadId: string,
  root: ParentNode = document,
  {editorKey}: Pick<CommentAnchorOptions, 'editorKey'> = {}
): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(getCommentElementSelector(threadId))).filter(
    (element) => belongsToEditorKey(element, editorKey)
  );

const getCommentElementsForAnchor = (
  threadId: string,
  root: ParentNode,
  options: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): HTMLElement[] =>
  filterElementsToPlanningCommentOrigin(
    threadId,
    getCommentElements(threadId, root, options),
    options.originId
  );

const hasVisibleRect = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const byDocumentPosition = (a: HTMLElement, b: HTMLElement) => {
  const position = a.compareDocumentPosition(b);
  if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
  if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
  return 0;
};

const getLexicalRootElement = (
  element: HTMLElement,
  root: ParentNode = document
): HTMLElement | null => {
  if (root instanceof HTMLElement) {
    if (root.hasAttribute('data-lexical-editor')) return root;
    const rootScopedEditor = Array.from(
      root.querySelectorAll<HTMLElement>('[data-lexical-editor]')
    ).find((editorRoot) => editorRoot.contains(element));
    if (rootScopedEditor) return rootScopedEditor;
  }

  const closestRoot = element.closest<HTMLElement>('[data-lexical-editor]');
  if (closestRoot) return closestRoot;

  return document.querySelector<HTMLElement>('[data-lexical-editor]');
};

const getDirectChildOfRoot = (
  element: HTMLElement,
  rootElement: HTMLElement
): HTMLElement | null => {
  let current: HTMLElement | null = element;
  while (current?.parentElement && current.parentElement !== rootElement) {
    current = current.parentElement;
  }

  return current?.parentElement === rootElement ? current : null;
};

const getHeadingLevel = (element: HTMLElement): number | null => {
  const match = element.tagName.match(/^H([1-6])$/);
  return match ? Number(match[1]) : null;
};

export const findCollapsedSectionHeadersForComment = (
  threadId: string,
  root: ParentNode = document,
  options: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): HTMLElement[] => {
  const commentElement = getCommentElementsForAnchor(threadId, root, options)
    .filter((element) => !hasVisibleRect(element))
    .sort(byDocumentPosition)[0];

  if (!commentElement) return [];

  const rootElement = getLexicalRootElement(commentElement, root);
  if (!rootElement) return [];

  const topLevelElement = getDirectChildOfRoot(commentElement, rootElement);
  if (!topLevelElement) return [];

  const children = Array.from(rootElement.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
  const targetIndex = children.indexOf(topLevelElement);
  if (targetIndex === -1) return [];

  const collapsedStack: Array<{element: HTMLElement; level: number}> = [];

  for (const child of children.slice(0, targetIndex)) {
    const headingLevel = getHeadingLevel(child);
    if (!headingLevel) continue;

    while (
      collapsedStack.length > 0 &&
      collapsedStack[collapsedStack.length - 1].level >= headingLevel
    ) {
      collapsedStack.pop();
    }

    if (child.getAttribute('data-collapsed') === 'true') {
      collapsedStack.push({element: child, level: headingLevel});
    }
  }

  return collapsedStack.map(({element}) => element);
};

export const findCollapsedSectionHeaderForComment = (
  threadId: string,
  root: ParentNode = document,
  options: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): HTMLElement | null =>
  findCollapsedSectionHeadersForComment(threadId, root, options).find(hasVisibleRect) ?? null;

export const findAttachedCommentElement = (
  threadId: string,
  root: ParentNode = document,
  options: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): HTMLElement | null => {
  const elements = getCommentElementsForAnchor(threadId, root, options);
  if (!elements.length) return null;

  const visibleElements = elements.filter(hasVisibleRect).sort(byDocumentPosition);

  return visibleElements[0] ?? elements[0];
};

export interface CommentPositionAnchor {
  anchor: HTMLElement;
  originalComment: HTMLElement;
}

export const findCommentPositionAnchor = (
  threadId: string,
  root: ParentNode = document,
  {includeHiddenFallback = true, editorKey, originId}: CommentAnchorOptions = {}
): CommentPositionAnchor | null => {
  const elements = getCommentElementsForAnchor(threadId, root, {editorKey, originId});
  const visibleElements = elements.filter(hasVisibleRect).sort(byDocumentPosition);
  if (visibleElements[0]) return {anchor: visibleElements[0], originalComment: visibleElements[0]};

  const firstComment = elements.sort(byDocumentPosition)[0];
  if (!firstComment) return null;

  const collapsedHeader = findCollapsedSectionHeaderForComment(threadId, root, {
    editorKey,
    originId,
  });
  if (collapsedHeader) return {anchor: collapsedHeader, originalComment: firstComment};

  return includeHiddenFallback ? {anchor: firstComment, originalComment: firstComment} : null;
};

export const findCommentPositionAnchorElement = (
  threadId: string,
  root: ParentNode = document,
  options: CommentAnchorOptions = {}
): HTMLElement | null => findCommentPositionAnchor(threadId, root, options)?.anchor ?? null;

export type ScrollTarget = HTMLElement | Window;

const isScrollable = (element: HTMLElement) => {
  const {overflowY} = window.getComputedStyle(element);
  return element.scrollHeight > element.clientHeight && /auto|scroll|overlay/.test(overflowY);
};

export const getScrollTargetForElement = (
  element: HTMLElement,
  root: ParentNode = document
): ScrollTarget => {
  const rootElement = root instanceof HTMLElement ? root : null;
  let current = element.parentElement;

  while (current) {
    if (isScrollable(current)) return current;
    if (rootElement && current === rootElement) break;
    current = current.parentElement;
  }

  return window;
};

const scrollTargetBy = (
  target: ScrollTarget,
  top: number,
  behavior: ScrollBehavior | undefined
) => {
  if (target === window) {
    window.scrollBy({top, behavior});
    return;
  }

  if (typeof target.scrollBy === 'function') {
    target.scrollBy({top, behavior});
    return;
  }

  const scrollableElement = target as HTMLElement;
  scrollableElement.scrollTop = scrollableElement.scrollTop + top;
};

export const getScrollTargetViewportTop = (target: ScrollTarget, viewportRatio: number) => {
  if (target === window) {
    return window.innerHeight * viewportRatio;
  }

  const scrollableElement = target as HTMLElement;
  return (
    scrollableElement.getBoundingClientRect().top + scrollableElement.clientHeight * viewportRatio
  );
};

export const getScrollTargetMetrics = (target: ScrollTarget) => {
  if (target === window) {
    const scrollingElement = document.scrollingElement ?? document.documentElement;
    const scrollHeight = Math.max(
      scrollingElement.scrollHeight,
      document.documentElement.scrollHeight,
      document.body?.scrollHeight ?? 0
    );
    const clientHeight = window.innerHeight;
    return {
      scrollTop: window.scrollY,
      scrollHeight,
      clientHeight,
      maxScrollTop: Math.max(0, scrollHeight - clientHeight),
    };
  }

  const scrollableElement = target as HTMLElement;
  return {
    scrollTop: scrollableElement.scrollTop,
    scrollHeight: scrollableElement.scrollHeight,
    clientHeight: scrollableElement.clientHeight,
    maxScrollTop: Math.max(0, scrollableElement.scrollHeight - scrollableElement.clientHeight),
  };
};

export const scrollAttachedCommentIntoView = (
  threadId: string,
  options: ScrollIntoViewOptions & Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {
    behavior: 'smooth',
    block: 'center',
  },
  root: ParentNode = document,
  anchorOptions: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): boolean => {
  const {editorKey, originId, ...scrollIntoViewOptions} = options;
  const element = findCommentPositionAnchorElement(threadId, root, {
    editorKey: anchorOptions.editorKey ?? editorKey,
    originId: anchorOptions.originId ?? originId,
  });
  if (!element) return false;

  element.scrollIntoView(scrollIntoViewOptions);
  return true;
};

export const scrollAttachedCommentToViewportRatio = (
  threadId: string,
  {
    behavior = 'smooth',
    viewportRatio,
    editorKey,
    originId,
  }: {behavior?: ScrollBehavior; viewportRatio: number} & Pick<
    CommentAnchorOptions,
    'editorKey' | 'originId'
  >,
  root: ParentNode = document,
  anchorOptions: Pick<CommentAnchorOptions, 'editorKey' | 'originId'> = {}
): boolean => {
  const element = findCommentPositionAnchorElement(threadId, root, {
    editorKey: anchorOptions.editorKey ?? editorKey,
    originId: anchorOptions.originId ?? originId,
  });
  if (!element) return false;

  const scrollTarget = getScrollTargetForElement(element, root);
  const elementTop = element.getBoundingClientRect().top;
  const targetViewportTop = getScrollTargetViewportTop(scrollTarget, viewportRatio);

  scrollTargetBy(scrollTarget, elementTop - targetViewportTop, behavior);
  return true;
};

const findHeadingNodeByHeadingId = (headingId: string): EnhancedHeadingNode | null => {
  for (const child of $getRoot().getChildren()) {
    if (child instanceof EnhancedHeadingNode && child.getHeadingId() === headingId) {
      return child;
    }
  }
  return null;
};

export const expandCollapsedSectionsContainingComment = ({
  threadId,
  editor,
  root = document,
  collabId,
  editorKey,
  originId,
}: {
  threadId: string;
  editor: LexicalEditor | null | undefined;
  root?: ParentNode;
  collabId?: string;
  editorKey?: string;
  originId?: string | null;
}): boolean => {
  if (!editor) return false;

  const collapsedHeadingIds = findCollapsedSectionHeadersForComment(threadId, root, {
    editorKey,
    originId,
  })
    .map((heading) => heading.getAttribute('data-heading-id'))
    .filter((headingId): headingId is string => Boolean(headingId));

  if (!collapsedHeadingIds.length) return false;

  editor.update(() => {
    collapsedHeadingIds.forEach((headingId) => {
      const node = findHeadingNodeByHeadingId(headingId);
      if (!node || !node.getCollapsed()) return;

      node.setCollapsed(false);
      if (collabId) {
        setPersistedHeadingCollapsed(collabId, headingId, false);
      }
      node.markDirty();
    });
  });

  return true;
};

/**
 * Scrolls to and highlights a comment thread by finding its DOM elements.
 */
export function scrollToAndHighlightComment(
  commentForDrawer: CommentForDrawer,
  setActiveComment: (state: {threadId: string} | null) => boolean,
  setFocusedThread: (threadId: string | undefined) => void
): void {
  try {
    // Find all DOM elements with the thread ID
    const commentElements = document.querySelectorAll(
      getCommentElementSelector(commentForDrawer.threadId)
    );

    if (commentElements.length === 0) {
      console.warn('No DOM elements found for thread:', commentForDrawer.threadId);
      return;
    }

    // Get the first element to scroll to
    const firstElement = commentElements[0] as HTMLElement;

    // Create a range selection that encompasses all comment elements
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();

      // Create a range that spans all comment elements
      const range = document.createRange();

      if (commentElements.length === 1) {
        // Single element - select its contents
        range.selectNodeContents(firstElement);
      } else {
        // Multiple elements - select from start of first to end of last
        const lastElement = commentElements[commentElements.length - 1];
        range.setStartBefore(firstElement);
        range.setEndAfter(lastElement);
      }

      selection.addRange(range);
    }

    // Scroll the first element into view
    firstElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    // Wait for scrolling to finish before activating the comment
    setTimeout(() => {
      // Set the focused thread and active comment
      if (!setActiveComment({threadId: commentForDrawer.threadId})) return;
      setFocusedThread(commentForDrawer.threadId);

      console.log('Activated comment thread:', commentForDrawer.threadId);
    }, 500); // Wait 500ms for smooth scroll to complete
  } catch (error) {
    console.error('Failed to scroll to and highlight comment:', error);
  }
}
